import AbstractAction from "./AbstractAction.js";
import InputValueDropdownAction from "./InputValueDropdownAction.js";

/**
 * @typedef {object} Item
 *
 * Item of the dropdown.
 * @property {function} getId - Function returning the id of the item
 * @property {function} getName - Function returning the name of the item
 * @property {function} match - Function invoked with a string to know if
 *   the item matches it. This is used to search in the dropdown
 */

/**
 * Action to insert an InputValue from a dropdown.
 *
 * @param {InputValueAction.type} type - Type of the input value to insert
 * @param {string} label - Label of the dropdown
 * @param {number} order - Order of the menu entry
 * @param {Promise|Item[]} items - Array of items that can be inserted.
 *   It can also be a Promise resolving to such an array
 * @param {string} [dataTestName] - Identifier used for testing purpose
 */
export default class InsertInputValueAction extends AbstractAction {
	constructor({ type, label, order, items, dataTestName }) {
		super(...arguments);

		this._type = type;
		this._label = label;
		this._order = order;
		this._items = items;
		this._dataTestName = dataTestName;
		this._dropdownAction = this._makeDropdownAction();
	}

	ensureDropdownMenuItemBuilder(builder) {
		this._dropdownAction.ensureDropdownMenuItemBuilder(builder);
	}

	getMenuItems(...spec) {
		return this._dropdownAction.getMenuItems(...spec);
	}

	_makeDropdownAction() {
		return new InputValueDropdownAction({
			label: this._label,
			items: Promise.resolve(this._items).then((allItems) =>
				allItems.map((item) => this._convertItemToDropdownEntry(item)),
			),
			order: this._order,
			dataTestName: this._dataTestName,
		});
	}

	_convertItemToDropdownEntry(item) {
		return {
			label: item.toLabel(),
			match: (string) => item.match(string),
			nodeSpec: {
				type: `inputValue_${this._type}`,
				attributes: { id: item.getId() },
			},
		};
	}
}
